import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import RichText from "components/basic/richText";
import Button from "components/basic/button";
import TextField from "components/basic/textField";
import Typography from "components/basic/typography";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SignUpCardLayout from "../../components/SignUpCardLayout";
import { useSnack } from "hooks/useSnack";
import { Account } from "../..";
import {
  useSendEmailVerificationMutation,
  useVerifyEmailCodeMutation,
} from "generated/graphql";

interface EmailVerificationFormProps {
  signUpData: Account;
  setSignUpData: React.Dispatch<React.SetStateAction<Account>>;
}

const EmailVerificationForm: React.FC<EmailVerificationFormProps> = ({
  signUpData,
  setSignUpData,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const showSnack = useSnack();
  const { t } = useTranslation("auth");

  const [, sendEmailVerification] = useSendEmailVerificationMutation();
  const [, verifyEmailCode] = useVerifyEmailCodeMutation();

  // Ref guard prevents double-fire in React StrictMode
  const hasSentRef = useRef(false);

  // Send the email verification code as soon as this page mounts
  useEffect(() => {
    if (hasSentRef.current) return;
    hasSentRef.current = true;

    const send = async () => {
      const response = await sendEmailVerification({ email: signUpData.email });
      if (response.data?.sendEmailVerification.errors) {
        showSnack(
          response.data.sendEmailVerification.errors[0].message ??
            t("verify.send_failed"),
          "error",
        );
      } else {
        showSnack(t("verify.sent", { email: signUpData.email }), "success");
      }
    };
    send();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [fields, setFields] = useState({
    emailVerificationCode: "",
  });

  const [errors, setErrors] = useState<{ [key: string]: string | null }>({
    emailVerificationCode: null,
  });

  // Resend cooldown — 60 seconds
  const [resendCooldown, setResendCooldown] = useState(60);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleResend = useCallback(async () => {
    if (resendCooldown > 0) return;
    const response = await sendEmailVerification({ email: signUpData.email });
    if (response.data?.sendEmailVerification.errors) {
      showSnack(
        response.data.sendEmailVerification.errors[0].message ??
          t("verify.resend_failed"),
        "error",
      );
    } else {
      showSnack(t("verify.new_sent", { email: signUpData.email }), "success");
      setResendCooldown(60);
    }
  }, [resendCooldown, sendEmailVerification, signUpData.email, showSnack, t]);

  const handleNextClick = useCallback(async () => {
    const code = fields.emailVerificationCode.trim();
    if (!code || code.length !== 6 || !/^\d{6}$/.test(code)) {
      setErrors({
        emailVerificationCode: t("common.code_digits"),
      });
      showSnack(t("common.fix_errors"), "error");
      return;
    }

    setErrors({ emailVerificationCode: null });

    const response = await verifyEmailCode({
      email: signUpData.email,
      code: fields.emailVerificationCode,
    });

    if (response.data?.verifyEmailCode.errors) {
      const msg =
        response.data.verifyEmailCode.errors[0].message ?? t("verify.invalid");
      setErrors({ emailVerificationCode: msg });
      showSnack(msg, "error");
      return;
    }

    navigate(
      signUpData.userType === "user"
        ? "?currentPage=userReviewForm"
        : "?currentPage=businessInfoForm",
    );
    setSignUpData((prev) => ({ ...prev, emailVerified: true }));
  }, [
    t,
    fields.emailVerificationCode,
    navigate,
    showSnack,
    verifyEmailCode,
    signUpData.email,
    signUpData.userType,
    setSignUpData,
  ]);

  const handleChange = useCallback(
    (name: string, value: string) => {
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: null }));
      }

      setFields((prev) => ({ ...prev, [name]: value }));
    },
    [errors],
  );

  return (
    <SignUpCardLayout>
      <div
        id="right section"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(2),
        }}
      >
        <Typography
          variant="h4"
          align="center"
          style={{ margin: `${theme.spacing(4)} 0` }}
        >
          {t("verify.title")}
        </Typography>

        <Typography variant="body2">
          <RichText
            t={t}
            i18nKey="verify.sent_intro"
            values={{ email: signUpData.email }}
            components={{ strong: <strong /> }}
          />
        </Typography>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <TextField
            size="tiny"
            style={{ width: "200px" }}
            textAlign="center"
            name="emailVerificationCode"
            value={fields.emailVerificationCode}
            placeHolder="000000"
            onChange={({ target: { name, value } }) =>
              handleChange(name, value)
            }
            {...(errors?.emailVerificationCode
              ? { error: errors.emailVerificationCode }
              : {})}
          />
        </div>

        <Typography variant="body2">
          <RichText
            t={t}
            i18nKey="verify.resend_hint"
            values={{
              action:
                resendCooldown > 0
                  ? t("verify.resend_in", { seconds: resendCooldown })
                  : t("verify.resend"),
            }}
            components={{
              resend: (
                <span
                  onClick={handleResend}
                  style={{
                    color:
                      resendCooldown > 0 ? theme.palette.grey[500] : "orange",
                    cursor: resendCooldown > 0 ? "default" : "pointer",
                    fontWeight: "bold",
                  }}
                />
              ),
            }}
          />
        </Typography>
      </div>

      <div>
        <div
          style={{
            margin: `${theme.spacing(2)} 0`,
            marginTop: theme.spacing(6),
          }}
        >
          <Button
            onClick={handleNextClick}
            variant="contained"
            color="warning"
            size="small"
            style={{ color: theme.palette.common.white }}
            fullWidth
          >
            {t("common.next")}
          </Button>
        </div>

        <Button
          onClick={() => navigate("?currentPage=addressForm")}
          variant="contained"
          size="small"
          style={{ color: theme.palette.common.white }}
          fullWidth
        >
          {t("common.back")}
        </Button>
      </div>
    </SignUpCardLayout>
  );
};

export default EmailVerificationForm;
